import React, { useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';

interface GoogleLoginProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

declare global {
  interface Window {
    google: any;
    gapi: any;
  }
}

export const GoogleLogin: React.FC<GoogleLoginProps> = ({ onSuccess, onError }) => {
  const { loginWithGoogle } = useAuth();
  const [isLoading, setIsLoading] = React.useState(false);
  const googleButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Load Google OAuth script if not already loaded
    if (!window.google) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogleAuth;
      document.head.appendChild(script);
    } else {
      initializeGoogleAuth();
    }

    return () => {
      // Cleanup Google auth if needed
    };
  }, []);

  const initializeGoogleAuth = () => {
    const clientId = (process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim();

    if (!clientId) {
      console.error('REACT_APP_GOOGLE_CLIENT_ID is not set!');
      return;
    }

    if (window.google && googleButtonRef.current) {
      try {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleGoogleResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        // Render the Google button directly instead of using prompt()
        window.google.accounts.id.renderButton(
          googleButtonRef.current,
          {
            theme: 'outline',
            size: 'large',
            // GSI accepts 200-400px; anything else logs an error and falls back
            width: Math.max(200, Math.min(400, googleButtonRef.current.offsetWidth || 320)),
            text: 'continue_with',
            shape: 'rectangular',
          }
        );

      } catch (error) {
        console.error('Failed to initialize Google OAuth:', error);
      }
    } else {
      console.error('Google Identity Services script not loaded');
    }
  };

  const handleGoogleResponse = async (response: any) => {
    try {
      setIsLoading(true);
      await loginWithGoogle(response.credential);
      onSuccess?.();
    } catch (error) {
      console.error('Google login failed:', error);
      onError?.(error instanceof Error ? error.message : 'Google login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full mb-4">
      {isLoading ? (
        <div className="w-full flex items-center justify-center gap-3 px-4 py-3 text-dark-text-secondary">
          <div className="w-[18px] h-[18px] border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
          <span>Signing in...</span>
        </div>
      ) : (
        <div ref={googleButtonRef} className="w-full flex justify-center"></div>
      )}
    </div>
  );
};

// Add spinner animation to document if not already present
if (typeof document !== 'undefined') {
  const existingStyle = document.getElementById('google-login-styles');
  if (!existingStyle) {
    const style = document.createElement('style');
    style.id = 'google-login-styles';
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }
}

export default GoogleLogin;
